/**
 * Per-class denomination + owned-label resolver.
 *
 * Layered on top of the existing client-wide `clientData.use_fund_language`
 * flag. The resolver lets a single class override the client-wide
 * denomination (e.g., ZEPH on a fund-language client) and lets admins
 * configure the displayed label per class or per client.
 *
 * Resolution priority for `getEffectiveDenomination`:
 *   1. `clientData.equity[classKey].denomination`            (per-class override)
 *   2. `'dollars'` when `clientData.use_fund_language === true`
 *   3. `'shares'`
 *
 * Resolution priority for `resolveOwnedLabel`:
 *   1. `clientData.equity[classKey].label_owned`             — only consulted when
 *      the same class has an explicit `denomination` override. The class-level
 *      label is intentionally coupled with the denomination override so an
 *      admin setting a per-class denomination also names the matching label.
 *   2. `clientData.language.label_owned`                     — client-wide override
 *   3. `'Contributed'` when `clientData.use_fund_language === true`
 *   4. `null` (the caller's existing platform default applies)
 *
 * Resolution priority for `resolveApprovedLabel` mirrors the above, one field
 * over (`owned` -> `approved`, i.e. "Capital Balance"/"Committed" on fund-
 * language clients):
 *   1. `clientData.equity[classKey].label_approved`          — only consulted when
 *      the same class has an explicit `denomination` override, same coupling
 *      rationale as `label_owned`.
 *   2. `clientData.language.label_approved`                  — client-wide override
 *   3. `'Committed'` when `clientData.use_fund_language === true`
 *   4. `null` (the caller's existing platform default applies)
 *
 * Compatibility contract: both resolvers are **additive**. Each only returns a
 * non-null label when its own new keys (`equity[classKey].label_owned`/
 * `label_approved` or `language.label_owned`/`label_approved`) are set, or
 * when the existing `use_fund_language` default applies. Neither consults
 * older language keys like `language.label_equitytype` or
 * `language.label_ownership` — those remain the call site's responsibility
 * and must be preserved as fallbacks beneath the resolver. Wiring should be:
 *     const resolved = resolveOwnedLabel(clientData, classKey);
 *     if (resolved !== null) return resolved;
 *     // existing branch logic with label_equitytype / label_ownership / etc.
 *
 * `hideApprovedField` is a separate, explicit opt-in — unlike the label
 * resolvers it does NOT fall back to a `use_fund_language` default, because
 * whether the `approved` balance is meaningful depends on whether the class
 * uses vesting schedules, not on its denomination. A class must set
 * `clientData.equity[classKey].hide_approved === true` to hide it; nothing
 * is hidden implicitly.
 *
 * All functions are pure. They tolerate missing input (null clientData,
 * missing equity/language maps, unknown classKey) without throwing — callers
 * frequently invoke them during early render before all data is hydrated.
 */

export const DENOMINATIONS = Object.freeze(['shares', 'dollars', 'percent']);

/**
 * Resolve a class's effective denomination.
 *
 * @param {object|null|undefined} clientData
 * @param {string|null|undefined} classKey
 * @returns {'shares'|'dollars'|'percent'}
 */
export function getEffectiveDenomination(clientData, classKey) {
	const classOverride = clientData?.equity?.[classKey]?.denomination;
	if (typeof classOverride === 'string' && DENOMINATIONS.includes(classOverride)) {
		return classOverride;
	}
	return clientData?.use_fund_language === true ? 'dollars' : 'shares';
}

/**
 * Resolve the label rendered for a class's `owned` field.
 *
 * Returns `null` (not an empty string) when no override applies, so callers
 * can distinguish "no resolver answer — use legacy default" from "configured
 * to empty string".
 *
 * @param {object|null|undefined} clientData
 * @param {string|null|undefined} classKey
 * @returns {string|null}
 */
export function resolveOwnedLabel(clientData, classKey) {
	const classEntry = clientData?.equity?.[classKey];

	// (1) per-class label only when the class also overrides denomination
	if (classEntry?.denomination && typeof classEntry?.label_owned === 'string' && classEntry.label_owned.length) {
		return classEntry.label_owned;
	}

	// (2) client-wide language override
	const clientLevel = clientData?.language?.label_owned;
	if (typeof clientLevel === 'string' && clientLevel.length) {
		return clientLevel;
	}

	// (3) existing use_fund_language default
	if (clientData?.use_fund_language === true) {
		return 'Contributed';
	}

	// (4) caller's existing platform default applies
	return null;
}

/**
 * Resolve the label rendered for a class's `approved` field (the
 * "Committed"/"Approved" balance alongside `owned`'s "Capital Balance"/"Owned").
 *
 * Returns `null` (not an empty string) when no override applies, so callers
 * can distinguish "no resolver answer — use legacy default" from "configured
 * to empty string".
 *
 * @param {object|null|undefined} clientData
 * @param {string|null|undefined} classKey
 * @returns {string|null}
 */
export function resolveApprovedLabel(clientData, classKey) {
	const classEntry = clientData?.equity?.[classKey];

	// (1) per-class label only when the class also overrides denomination
	if (classEntry?.denomination && typeof classEntry?.label_approved === 'string' && classEntry.label_approved.length) {
		return classEntry.label_approved;
	}

	// (2) client-wide language override
	const clientLevel = clientData?.language?.label_approved;
	if (typeof clientLevel === 'string' && clientLevel.length) {
		return clientLevel;
	}

	// (3) existing use_fund_language default
	if (clientData?.use_fund_language === true) {
		return 'Committed';
	}

	// (4) caller's existing platform default applies
	return null;
}

/**
 * Whether a class's `approved` balance should be hidden from admin UI
 * (both display and input). Explicit opt-in only — see file header.
 *
 * When hidden, callers must keep `approved` in sync with `owned` themselves
 * (the resolver only answers the display question, it doesn't touch data).
 *
 * @param {object|null|undefined} clientData
 * @param {string|null|undefined} classKey
 * @returns {boolean}
 */
export function hideApprovedField(clientData, classKey) {
	return clientData?.equity?.[classKey]?.hide_approved === true;
}
